<template>
  <div class="container-onboarding">
    <!--Indicator-->
    <Indicator v-if="onboardingStep >= 2" :indicators="listStepIndicator" />

    <!--Component-->
    <component
      :is="currentOnboardingStep.component"
      v-model="onboardingForm"
      @close="handleNext"
      @moveDashboard="moveToDashboard"
    />

    <!--Footer-->
    <div class="onboarding-footer fadeinOnboarding">
      <button
        v-if="onboardingStep >= 2 && onboardingStep < ONBOARDING_STEP_MAP.length - 1"
        class="btn-prev gray-btn l-btn"
        @click="handlePrev"
      >
        {{ t('single_language_back') }}
      </button>

      <ButtonProcess full-width :loading="isProcessing" :disabled="isDisabledButtonProcess" @click="handleNext">
        {{ onboardingStep === 1 ? t('entrance') : t(currentOnboardingStep.nextButtonKey) }}
      </ButtonProcess>
    </div>

    <teleport to="body">
      <SelfServiceModalPolicy v-if="isVisibleModalPolicy" />
    </teleport>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';

import { cloneDeep } from 'lodash';

import { createChatRoom, signupOAuth, updateEmployee } from '@/api/apiService';
import SelfServiceModalPolicy from '@/components/domains/self-service/common/SelfServiceModalPolicy.vue';
import ButtonProcess from '@/components/shared/base/ButtonProcess.vue';
import Indicator from '@/components/shared/base/Indicator.vue';
import { useModalPolicy } from '@/composables/domains/self-service/useModalPolicy';
import { useFileInput } from '@/composables/shared/useFileInput';
import { useGtag } from '@/composables/shared/useGtag';
import { useNotification } from '@/composables/shared/useNotification';
import { getLanguageByCode } from '@/static/languages';
import { useAudioStore } from '@/stores/audio';
import { useCountryStore } from '@/stores/country';
import { useToastStore } from '@/stores/toast';
import { useUserStore } from '@/stores/user';
import { ISelfServiceOnboardingForm, ISelfServiceOnboardingStep } from '@/types/components';
import { CHAT_PRICE_KEY_MAP, CHAT_OPTION_PRESETS, SERVICE_PRICE_MAP } from '@/utils/constants/chat';
import { defaultOnboardingForm, ONBOARDING_STEP_MAP } from '@/utils/constants/selfService';

const { t } = useI18n();

const userStore = useUserStore();
const audioStore = useAudioStore();
const toastStore = useToastStore();
const countryStore = useCountryStore();

const route = useRoute();
const router = useRouter();
const { listImageDefault } = useFileInput();
const { alertNetworkError } = useNotification();
const { isVisibleModalPolicy } = useModalPolicy();

const { trackEventClick } = useGtag();

const isProcessing = ref<boolean>(false); // 처리중

const onboardingStep = ref<number>(0); // 온보딩 과정 진행 단계
const onboardingForm = ref<ISelfServiceOnboardingForm>({
  ...cloneDeep(defaultOnboardingForm),
  nickname: userStore.userInfo?.nickname || '',
  callLogoImage: listImageDefault[0],
  languageCode: getLanguageByCode(userStore.pageLanguage),
  referralCode: route.query.referralCode?.toString() || '',
}); // 온보딩 폼

const routeQuery = ref<{
  code?: string;
  planId?: string;
  languageAway?: boolean;
}>({});

// 현재 온보딩 스탭
const currentOnboardingStep = computed<ISelfServiceOnboardingStep>(() => {
  return ONBOARDING_STEP_MAP[onboardingStep.value] || ({} as ISelfServiceOnboardingStep);
});

// 온보딩 인디케이터
const listStepIndicator = computed<boolean[]>(() => {
  return Array.from({ length: ONBOARDING_STEP_MAP.length - 2 }, (_, i) => onboardingStep.value === 2 + i);
});

// callType에 따른 통화방 생성 스킵 단계
const skipCreateChatSteps = computed<number>(() => {
  return (
    {
      GENERAL: 1,
      CONFERENCE: 1,
      SINGLE: 3,
      AUDIENCE: 3,
    }[onboardingForm.value.callType] || 2
  );
});

// 2자이상 문자열 검사
const validateString = (str: string): boolean => {
  return !!str && str.trim().length >= 2;
};

// '다음' 버튼 비활성화 여부
const isDisabledButtonProcess = computed<boolean>(() => {
  const { validateKey = [] } = currentOnboardingStep.value;
  return (
    isProcessing.value ||
    validateKey.some((key) => {
      if (['nickname', 'title'].includes(key)) {
        return !validateString(onboardingForm.value[key].toString());
      }
      return !onboardingForm.value[key];
    })
  );
});

// 이전 클릭 시, 실행되는 이벤트 입니다.
const handlePrev = () => {
  trackEventClick(`onboarding_${currentOnboardingStep.value.stepKey}`, '이전');

  switch (currentOnboardingStep.value.stepKey) {
    case 'LANGUAGE':
      resetLanguage();
      onboardingStep.value--;
      break;
    case 'CHAT_NAME':
      onboardingStep.value -= skipCreateChatSteps.value;
      break;
    default:
      onboardingStep.value--;
      break;
  }
};

// 다음 클릭 시, 실행되는 이벤트입니다.
const handleNext = async (step?: number) => {
  if (step) {
    onboardingStep.value += step;
  } else {
    trackEventClick(`onboarding_${currentOnboardingStep.value.stepKey}`, '다음');

    switch (currentOnboardingStep.value.stepKey) {
      case 'AGREEMENT':
        onboardingStep.value += (await requestCreateUser()) ? 1 : 0;
        break;
      case 'NICKNAME':
        onboardingStep.value += (await requestChangeNickname()) ? 1 : 0;
        break;
      case 'LANGUAGE':
        onboardingStep.value += setLanguage() ? 1 : 0;
        break;
      case 'INTERPRETATION':
        onboardingStep.value += setPricingDetails() ? skipCreateChatSteps.value : 0;
        break;
      case 'CHAT_NAME':
        onboardingStep.value += (await requestCreateChatRoom()) ? 1 : 0;
        break;
      case 'READY':
        await moveToChatRoom();
        break;
      default:
        onboardingStep.value++;
        break;
    }
  }
};

// 필요한 query가 없을때, 리다이렉트 시킵니다.
const skipRegisterStep = () => {
  if (!route.query.oAuth || !route.query.accessToken) {
    handleNext(1);
  }
};

// 대시보드 페이지로 이동합니다.
const moveToDashboard = () => {
  trackEventClick('onboarding', '대시보드로 가기');
  router.push({ name: 'ss-dashboard' });
};

// callType에 맞는 채팅방 페이지로 이동합니다.
const moveToChatRoom = async () => {
  const { callType = '' } = onboardingForm.value;
  const name = callType ? `ss-chat-${callType.toLowerCase()}` : '';

  if (name && router.hasRoute(name)) {
    await router.replace({ name, query: routeQuery.value });
  }
};

/**
 * @desc 페이지 언어를 설정합니다.
 * @return {boolean}
 * */
const setLanguage = async (): boolean => {
  if (onboardingForm.value.languageCode !== userStore.pageLanguage) {
    const userId = String(userStore.userInfo?.userId ?? '').trim();
    if (!userId) {
      throw new Error('Missing userId');
    }

    const res = await updateEmployee(userId, null, {
      body: JSON.stringify({
        nickname: onboardingForm.value.nickname,
        languageCode: onboardingForm.value.languageCode,
        groupIds: [],
        sampleRate: '16000',
      }),
      voice: '',
    });

    if (!res.success) {
      throw new Error();
    }

    await userStore.fetchUserInfo();
    return true;
  }

  return true;
};

/**
 * @desc 페이지 언어를 설정합니다.
 * */
const resetLanguage = () => {
  if (onboardingForm.value.languageCode !== userStore.pageLanguage) {
    audioStore.selectLanguage(onboardingForm.value.languageCode);
  }
};

/**
 * @desc 'callType' 을 기반으로 가격정보를 조회합니다.
 * @return {boolean}
 * */
const setPricingDetails = (): boolean => {
  let isSuccess = false;

  const { callType = '' } = onboardingForm.value;
  const id = CHAT_PRICE_KEY_MAP[callType];
  const { limitAttendee: maxParticipants = 0 } =
    Object.values(SERVICE_PRICE_MAP).find(({ prefix }) => id === prefix) || {};

  if (callType && id && maxParticipants) {
    isSuccess = true;
    Object.assign(onboardingForm.value, {
      ...onboardingForm.value,
      id,
      maxParticipants,
    });
  }

  if ('AUDIENCE' === callType) {
    Object.assign(routeQuery.value, {
      code: userStore.userId,
      planId: 'SCB_A1',
    });
  } else if ('SINGLE' === callType) {
    Object.assign(routeQuery.value, {
      languageAway: true,
    });
  }

  return isSuccess;
};

/**
 * @desc 사용자 생성 요청합니다.
 * @return {boolean}
 * */
const requestCreateUser = async (): Promise<boolean> => {
  let isSuccess = false;
  isProcessing.value = true;

  try {
    const { oAuth = '', userId = '', accessToken = '', email = '' } = route.query;
    const {
      isAgreedStorage: storeAgreement,
      isAgreedMarketing: marketingAgreement,
      countryCode,
      referralCode,
    } = onboardingForm.value;

    // 3rdParty 회원가입
    const res = await signupOAuth({
      oAuth: (oAuth || '').toString(),
      userId: userId?.toString() || null, // userId 없는 google 의 경우 반드시 null로 보내야함
      accessToken: (accessToken || '').toString(),
      countryCode,
      storeAgreement,
      marketingAgreement,
      referralCode,
      languageCode: audioStore.languageSelected?.code || userStore.pageLanguage,
      email: (email || '').toString(),
    });

    if (res.success) {
      localStorage.setItem('authToken', res.data);
      sessionStorage.setItem('authToken', res.data);
      localStorage.setItem('loginType', 'SELF_SERVICE');

      // 임시로 넣은 값입니다. 추후 API로 받은 값을 바인딩할 수 있습니다.
      localStorage.setItem('userType', 'HOST');
      localStorage.setItem('initializedPassword', false);

      // 사용자 정보 조회
      await userStore.fetchUserInfo();
      const { nickname = '' } = userStore.userInfo || {};
      onboardingForm.value.nickname = nickname;
      isSuccess = true;
    } else if (res.errorCode === 4040) {
      toastStore.error(t('error_referral_code'));
    } else {
      throw new Error();
    }
  } catch (error) {
    console.error('SIGN UP ERROR:', error);
    alertNetworkError();
  }

  isProcessing.value = false;
  return isSuccess;
};

/**
 * @desc 사용자 닉네임 변경 요청합니다.
 * @return {boolean}
 * */
const requestChangeNickname = async (): Promise<boolean> => {
  let isSuccess = false;
  const { nickname = '' } = userStore.userInfo || {};

  if (nickname !== onboardingForm.value.nickname) {
    try {
      const userId = String(userStore.userInfo?.userId ?? '').trim();
      if (!userId) throw new Error('Missing userId');

      const res = await updateEmployee(userId, null, {
        body: JSON.stringify({
          nickname: onboardingForm.value.nickname,
          languageCode: userStore.pageLanguage,
          groupIds: [],
          sampleRate: '16000',
        }),
        voice: '',
      });
      if (!res.success) {
        throw new Error();
      }
      await userStore.fetchUserInfo();
      isSuccess = true;
    } catch (error) {
      console.error('UPDATE NICKNAME ERROR:', error);
      alertNetworkError();
    }
  } else {
    isSuccess = true;
  }

  return isSuccess;
};

/**
 * @desc 통역방 생성을 요청합니다.
 * @return {boolean}
 * */
const requestCreateChatRoom = async (): Promise<boolean> => {
  let isSuccess = false;
  isProcessing.value = true;

  try {
    const { id = '', callType = '', maxParticipants = '', title = '' } = onboardingForm.value;

    // 통역방 옵션
    const options = CHAT_OPTION_PRESETS[callType] || {};

    const formData = new FormData();
    formData.append(
      'body',
      JSON.stringify({
        plan: `${id + maxParticipants}`,
        callType,
        title,
        description: '',
        keywords: [],
        options,
        partnerGroupId: null,
        advancedOptions: null,
      })
    );

    const res = await createChatRoom(formData);
    if (res.success) {
      const { planId = '', code = '' } = res.data[0];
      Object.assign(routeQuery.value, { planId, code });
      isSuccess = true;
    }
  } catch {
    isSuccess = false;
  }

  isProcessing.value = false;
  return isSuccess;
};

onMounted(async () => {
  await countryStore.requestCountries();
  skipRegisterStep();
});
</script>

<style scoped lang="scss">
@import '@/assets/sass/theme/sconchat';

.container-onboarding {
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  align-items: center;
  height: 100dvh;
  word-break: keep-all;
  position: relative;
}

::v-deep(.container-onboarding-content) {
  display: flex;
  flex-direction: column;
  width: 30rem;
  height: 75dvh;
  max-height: 75dvh;
  min-height: 29rem;
  padding-bottom: 1rem;

  @include respond-to(mobile-portrait) {
    width: 100%;
    padding-right: 1.5rem;
    padding-left: 1.5rem;
  }
}

::v-deep(.wrapper-title) {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  margin-bottom: 2rem;
}

::v-deep(.fadeinOnboarding) {
  opacity: 0;
  animation: fadeInOnboarding 0.8s ease forwards;
  animation-delay: 0.5s;
}

::v-deep(.late) {
  animation-delay: 1s;
}

@keyframes fadeInOnboarding {
  to {
    opacity: 1;
  }
}

::v-deep(.wrapper-onboarding-content) {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  margin-bottom: 2rem;
}

::v-deep(.profile-selector) {
  width: fit-content;
}

::v-deep(.profile-selector .profile-current) {
  margin-bottom: unset;
}

::v-deep(.wrapper-content-detail) {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

::v-deep(.link-dashboard) {
  margin-top: 1rem;
}

.onboarding-footer {
  display: flex;
  width: 30rem;
  margin: 1.5rem 0;
  gap: 0.5rem;

  @include respond-to(mobile-portrait) {
    width: 100%;
    padding-right: 1.5rem;
    padding-left: 1.5rem;
  }
}

.btn-prev {
  width: 8rem;
  padding: 0.75rem;
}

.btn-full {
  flex: 1;
}
</style>
